/**
 * The API's base URL, read once from the build-time environment.
 *
 * Only `VITE_`-prefixed variables are inlined by Vite; anything else read from
 * `import.meta.env` here is `undefined` in a production build exactly as much
 * as in development. `main.tsx` catches the throw, because a module-scope one
 * blanks the page with nothing on screen to say why.
 */
export function getBaseUrl(): string {
  const url: unknown = import.meta.env.VITE_API_BASE_URL;
  if (typeof url !== "string" || url === "") {
    throw new Error("VITE_API_BASE_URL no está configurado");
  }
  return url;
}

/**
 * The Turnstile sitekey this build draws its widget with, or undefined.
 *
 * Undefined is a first-class answer, not a misconfiguration: an app whose API
 * has no captcha switched on has no widget to draw, and the forms must work
 * exactly as they did. Unlike VITE_API_BASE_URL this therefore does not throw —
 * there is nothing broken about its absence.
 *
 * Public in the literal sense. It is printed into the HTML of every page that
 * renders the widget, which is why it lives in the manifest and reaches the
 * bundle at build time rather than living in a secret store. The SECRET key is
 * the server's and never comes near this file.
 */
export function getTurnstileSitekey(): string | undefined {
  const key: unknown = import.meta.env.VITE_TURNSTILE_SITEKEY;
  return typeof key === "string" && key !== "" ? key : undefined;
}

/**
 * The Google OAuth web client id behind «Continuar con Google», or undefined.
 *
 * Same shape as `getTurnstileSitekey`: absence is not an error, it means this
 * build offers no Google sign-in and draws nothing. The id is public -- it
 * travels in the bundle and in every Google request -- so it is not a secret.
 */
export function getGoogleClientId(): string | undefined {
  const id: unknown = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  return typeof id === "string" && id !== "" ? id : undefined;
}

/**
 * The Apple Services ID behind «Continuar con Apple» (`com.fusiqo.vozqi.web`),
 * or undefined.
 *
 * Same shape as `getGoogleClientId`: absence means this build offers no Apple
 * sign-in and draws no button, and every screen works as before. The id is
 * public -- it travels in the page and in the address sent to Apple.
 */
export function getAppleServicesId(): string | undefined {
  const id: unknown = import.meta.env.VITE_APPLE_SERVICES_ID;
  return typeof id === "string" && id !== "" ? id : undefined;
}

/**
 * The sitekey the operator login draws its widget with (spec 2026-09-23
 * §3.2), or undefined. The public profile's review report (P3 Enmienda 4)
 * draws the same widget, with its own action: one widget, one secret, and the
 * variable keeps its name because renaming it means a new build secret for
 * nothing.
 *
 * A VARIABLE OF ITS OWN, not VITE_TURNSTILE_SITEKEY: the API checks no token
 * on sign-up, resend or forgot (app.ts passes the auth router null),
 * so setting the shared one would draw a widget on those screens that stops
 * nobody. Undefined here means the login cannot be completed, which is the
 * honest answer for a build nobody configured for it.
 */
export function getOpsTurnstileSitekey(): string | undefined {
  const key: unknown = import.meta.env.VITE_OPS_TURNSTILE_SITEKEY;
  return typeof key === "string" && key !== "" ? key : undefined;
}

/**
 * The DSN this build reports render errors to, or undefined.
 *
 * Same shape as `getTurnstileSitekey`, for the same reason: a build with no
 * DSN configured must work exactly as it does today, so absence is not an
 * error. The DSN is public in the same literal sense too -- it travels in
 * the bundle -- but it is not hardcoded, because a hardcoded one would send
 * lab and production to the same Sentry project with no way to tell them
 * apart short of editing code (see `getSentryEnvironment` for the field that
 * actually distinguishes them).
 */
export function getSentryDsn(): string | undefined {
  const dsn: unknown = import.meta.env.VITE_SENTRY_DSN;
  return typeof dsn === "string" && dsn !== "" ? dsn : undefined;
}

/**
 * Which Sentry `environment` this build reports under -- "lab" or "prod",
 * never a second Sentry project.
 *
 * Defaults to "lab" when unset, matching `tenant: lab` in app.yaml: every
 * deploy this app has today is that tenant, and a deploy that forgets to set
 * `VITE_SENTRY_ENVIRONMENT` should read as the tenant it actually is rather
 * than fall into Sentry's own default of "production", which would be a lie.
 */
export function getSentryEnvironment(): string {
  const env: unknown = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  return typeof env === "string" && env !== "" ? env : "lab";
}

/**
 * Which build of the web client this is: `web-` and the first eight
 * characters of the commit the deploy workflow built it from (`VITE_RELEASE`),
 * or `web-dev` locally. Sent to the API as `x-client-release` and to Sentry as
 * `release`, so an error on either side names the exact bundle.
 */
export function getRelease(): string {
  const release: unknown = import.meta.env.VITE_RELEASE;
  return `web-${typeof release === "string" && release !== "" ? release.slice(0, 8) : "dev"}`;
}

/**
 * The Google Maps key, or undefined: the public profile draws its map with it
 * («Dónde estamos», Maps Embed API), and «Ubicar en el
 * mapa» loads the Maps JavaScript and Geocoding APIs with it.
 *
 * Public in the literal sense -- it travels in the page -- and restricted by
 * Google to those three APIs and to this site's referrers, so it is a GitHub
 * variable, not a secret (docs/contingencia/INVENTARIO.md). Unset (a dev
 * machine without one), the profile shows the address and its two buttons
 * without the map, and the settings offer no picker: config, not a fault.
 */
export function getMapsEmbedKey(): string | undefined {
  const key: unknown = import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY;
  return typeof key === "string" && key !== "" ? key : undefined;
}
